import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { TestimonialCarousel } from "@/components/TestimonialCarousel";
import { RECONNECTION, IMAGES } from "@/content";

export default function Reconnection() {
  const [tilt] = useState(0);
  const R = RECONNECTION;
  return (
    <Layout page="the-reconnection">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <img src={IMAGES.silhouette} alt="Silhouette in shadow" className="absolute inset-0 h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/70 via-ink-900/45 to-ink-900" />
        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 sm:px-8 pt-28">
          <motion.span className="label" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>Why</motion.span>
          <motion.h1 className="font-display font-black text-white tracking-tighter mt-5 leading-[0.95]"
            style={{ fontSize: "clamp(3rem, 11vw, 9rem)" }}
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}>
            The<br />Reconnection
          </motion.h1>
          <motion.p className="font-display text-xl sm:text-2xl text-ice/85 mt-8 max-w-2xl leading-snug"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}>
            {R.intro}
          </motion.p>
        </div>
      </section>

      {/* Narrative: disconnection -> reconnection. Clean single-column read. */}
      <section className="relative py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 space-y-8">
          {R.paragraphs.map((p, i) => {
            const isKey = i === 4;
            return (
              <Reveal key={i} delay={0.03 * i}>
                <p className={`leading-relaxed ${isKey ? "font-display text-2xl sm:text-3xl font-medium text-white leading-snug border-l-2 border-cyan/50 pl-6" : "text-ice/80 text-lg"}`}>
                  {p}
                </p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Founder quote — major brand statement */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 mesh-top" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-2 relative">
            <div className="absolute inset-0 bg-electric/15 blur-3xl rounded-full" />
            <img src={IMAGES.founder} alt="Founder portrait placeholder" className="relative rounded-3xl w-full object-cover border border-white/10 aspect-[3/4]" loading="lazy" />
          </div>
          <div className="md:col-span-3">
            <span className="font-display text-7xl text-electric/40 leading-none">"</span>
            <blockquote className="font-display text-2xl sm:text-3xl md:text-4xl font-medium text-white leading-snug -mt-6">
              {R.founderQuote}
            </blockquote>
            <p className="label mt-8 text-cyan">Alexia — Founder</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <TestimonialCarousel items={R.testimonials} testId="reconnection-testimonials" />
        </div>
      </section>

      {/* Forward link */}
      <section className="relative pb-32 text-center">
        <Reveal>
          <Link to="/classes" data-testid="explore-classes-link"
            className="group inline-flex items-center gap-3 font-display text-3xl sm:text-4xl font-bold text-white hover:text-cyan transition-colors">
            Explore the Classes <ArrowRight className="group-hover:translate-x-2 transition-transform" size={30} />
          </Link>
        </Reveal>
      </section>
    </Layout>
  );
}
